import React, { useEffect, useMemo, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { garageSpecialRequestService } from '../../../../services/apiService';

const STAGE_OPTIONS = ['intake', 'design', 'build', 'reveal', 'closed'];
const STATUS_OPTIONS = ['new', 'reviewing', 'quoted', 'approved', 'in_progress', 'completed', 'rejected', 'closed'];

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const items = await garageSpecialRequestService.listMyTransformationProjects();
      setRows(items || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (stageFilter === 'all') return rows;
    return rows.filter((r) => r.stage === stageFilter);
  }, [rows, stageFilter]);

  const updateRecord = async (id, patch) => {
    setSavingId(id);
    try {
      await garageSpecialRequestService.updateMyTransformationStatus(id, patch);
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));
    } catch (_) {
      // no-op
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Transformation Projects</h2>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm sm:w-56"
        >
          <option value="all">All stages</option>
          {STAGE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
            <FaSpinner className="animate-spin" /> Loading projects...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-gray-500">No transformation projects yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Donor Vehicle</th>
                <th className="px-4 py-3 text-left">Vision</th>
                <th className="px-4 py-3 text-left">Stage</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => {
                const id = item._id;
                const vehicle = [item.donorVehicle?.make, item.donorVehicle?.model, item.donorVehicle?.year]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={id}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{item.clientInfo?.fullName || '—'}</div>
                      <div className="text-xs text-gray-500">{item.clientInfo?.phone || '—'}</div>
                      <div className="text-xs text-gray-500">{item.clientInfo?.projectNickname || ''}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700">{vehicle || '—'}</td>
                    <td className="max-w-md px-4 py-3 align-top text-gray-700">
                      <div className="line-clamp-3">{item.vision?.description || '—'}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={item.stage || 'intake'}
                        disabled={savingId === id}
                        onChange={(e) => updateRecord(id, { stage: e.target.value })}
                        className="w-28 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold"
                      >
                        {STAGE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={item.status || 'new'}
                        disabled={savingId === id}
                        onChange={(e) => updateRecord(id, { status: e.target.value })}
                        className="w-32 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-600">{formatDate(item.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
